import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Package, AlertTriangle, Check, Loader2, Printer, Ban } from 'lucide-react';
import { api } from '../../../api/client';
import { hiRes, BatchData, MultiProduct, ScanResult } from '../shared';
import { useCan } from '../../../lib/rbac';
import { AmazonOrdersStrip } from '../components/AmazonOrdersStrip';

// Paquete Amazon multi-producto: un tracking físico que trae N compras, cada una
// asociada a su propia orden ML. Flujos: tocar tarjeta (1×1, reabre el scan con
// purchase_id → popup clásico) o "Imprimir todas" → modo checklist (cada etiqueta
// ML escaneada cierra su orden vía /add-and-close, guards del backend intactos).
export function AmazonMultiModal({ result, batches, noBagReason, onSelectProduct, onClose, onAllDone }: {
  result: ScanResult; batches: BatchData[];
  noBagReason?: string | null;
  onSelectProduct: (purchaseId: number) => void;
  onClose: () => void;
  onAllDone: () => void;
}) {
  const { t } = useTranslation();
  const canOperate = useCan()('warehouse.operate');
  const products: MultiProduct[] = result.products || [];

  const [checklistMode, setChecklistMode] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printResults, setPrintResults] = useState<Record<number, { ok: boolean; error?: string }>>({});
  const [closedShipments, setClosedShipments] = useState<Set<string>>(new Set());
  const [scanCode, setScanCode] = useState('');
  const [scanBusy, setScanBusy] = useState(false);
  const [scanError, setScanError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Elegible para procesar: pendiente, con venta asociada, no cancelada
  const eligible = useMemo(() => products.filter(p =>
    !p.already_processed && !p.unlinked &&
    p.order?.status !== 'cancelled' && p.shipment?.status !== 'cancelled'
  ), [products]);
  const pendingNow = eligible.filter(p => !closedShipments.has(String(p.shipment?.ml_shipment_id)));
  const doneCount = (result.processed_count || 0) + closedShipments.size;

  const handlePrintAll = async () => {
    setPrinting(true);
    setScanError('');
    try {
      const res: any = await api.post('/warehouse/multi/print-labels', {
        orders: eligible.map(p => ({ order_id: p.order!.order_id, seller_id: p.seller.id })),
      });
      if (res.success) {
        const map: Record<number, { ok: boolean; error?: string }> = {};
        for (const r of res.results || []) {
          map[r.order_id] = r.error ? { ok: false, error: r.error } : { ok: true };
        }
        setPrintResults(map);
        setChecklistMode(true);
        setTimeout(() => inputRef.current?.focus(), 150);
      } else {
        setScanError(res.error || t('wh.multiLabelError'));
      }
    } catch (err: any) {
      setScanError(err.message || t('wh.multiLabelError'));
    } finally {
      setPrinting(false);
    }
  };

  const handleChecklistScan = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = scanCode.trim();
    if (!code || scanBusy) return;
    setScanBusy(true);
    setScanError('');
    try {
      // Paso 1: ¿a cuál de los envíos pendientes corresponde esta etiqueta?
      const resolve: any = await api.post('/warehouse/multi/resolve-close-code', {
        code,
        candidates: pendingNow.map(p => ({
          ml_shipment_id: p.shipment!.ml_shipment_id,
          seller_id: p.seller.id,
        })),
      });
      const matchedId = resolve?.matched_ml_shipment_id;
      const product = matchedId
        ? pendingNow.find(p => String(p.shipment?.ml_shipment_id) === String(matchedId))
        : null;
      if (!product) {
        setScanError(t('wh.multiNoMatch'));
        return;
      }
      const targetBatch = batches.find(b => b.seller_id === product.seller.id);
      if (!targetBatch) {
        setScanError(`Sin lote abierto para ${product.seller.nickname}`);
        return;
      }
      // Paso 2: add+close por la ruta existente — reutiliza TODOS los guards
      // (etiqueta vs envío, cancelada, idempotencia, lote abierto, bolsas).
      const res: any = await api.post('/warehouse/add-and-close', {
        close_code: code,
        batch_id: targetBatch.id,
        seller_id: product.seller.id,
        ml_order_id: product.order!.ml_order_id,
        ml_shipment_id: product.shipment!.ml_shipment_id,
        order_id: product.order!.order_id,
        amazon_tracking: result.package?.tracking_number,
        amazon_purchase_id: product.purchase_id,
        asin: product.asin,
        title: product.title,
        quantity: product.quantity || 1,
        source: 'multi_scan',
        ...(noBagReason ? { no_bag_reason: noBagReason } : {}),
      });
      if (res.success && res.closed) {
        const next = new Set(closedShipments);
        next.add(String(product.shipment!.ml_shipment_id));
        setClosedShipments(next);
        if (eligible.every(p => next.has(String(p.shipment?.ml_shipment_id)))) {
          onAllDone();
        }
      } else {
        setScanError(res.error || t('wh.multiNoMatch'));
      }
    } catch (err: any) {
      setScanError(err.message || t('wh.multiNoMatch'));
    } finally {
      setScanBusy(false);
      setScanCode('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const cardState = (p: MultiProduct): { style: string; badge: string | null; icon: React.ReactNode; clickable: boolean } => {
    const closed = !!p.already_processed || closedShipments.has(String(p.shipment?.ml_shipment_id));
    if (p.order?.status === 'cancelled' || p.shipment?.status === 'cancelled')
      return { style: 'border-red-400 bg-red-50 dark:bg-red-900/20 opacity-90', badge: t('wh.multiCancelledCard'), icon: <Ban size={16} className="text-red-600" />, clickable: false };
    if (closed)
      return { style: 'border-green-400 bg-green-50 dark:bg-green-900/20 opacity-70', badge: t('wh.multiProcessed'), icon: <Check size={16} className="text-green-600" />, clickable: false };
    if (p.unlinked)
      return { style: 'border-amber-400 bg-amber-50 dark:bg-amber-900/20', badge: t('wh.multiUnlinked'), icon: <AlertTriangle size={16} className="text-amber-600" />, clickable: false };
    if (p.claim)
      return { style: 'border-orange-400 bg-orange-50 dark:bg-orange-900/20 cursor-pointer', badge: t('wh.multiClaimCard'), icon: <AlertTriangle size={16} className="text-orange-600" />, clickable: true };
    return { style: 'border-[var(--color-border)] bg-[var(--color-bg)] hover:border-blue-400 cursor-pointer', badge: null, icon: null, clickable: true };
  };

  const allDone = eligible.length === 0 || eligible.every(p => closedShipments.has(String(p.shipment?.ml_shipment_id)));

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="flex flex-col rounded-2xl shadow-2xl w-full max-w-5xl max-h-[96vh] bg-[var(--color-bg-card)]" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="shrink-0 px-4 pt-4">
          <div className="flex items-center gap-3">
            <Package size={24} className="text-blue-600" />
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold text-[var(--color-text)]">
                {t('wh.multiPackage')} — {products.length} {t('wh.multiProducts')}, {doneCount} {t('wh.multiDone')}
              </h2>
              <p className="text-sm text-[var(--color-text-secondary)] font-mono truncate">
                {result.package?.tracking_number}
                {result.package?.carrier ? ` · ${result.package.carrier}` : ''}
              </p>
            </div>
            <button onClick={onClose} className="px-3 py-2 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text)]">✕</button>
          </div>
        </div>
        {/* 2026-10-17 — orden(es) Amazon del paquete + "Solicitar devolución en Amazon" */}
        <AmazonOrdersStrip
          orders={(result.package?.amazon_order_ids ?? []).map(id => ({ amazon_order_id: id, ml_order_id: null, asin: null, amazon_status: null }))}
        />
        <div className="shrink-0 px-4 pb-3 border-b border-[var(--color-border)]">
          {!checklistMode && pendingNow.length > 0 && canOperate && (
            <div className="mt-3 flex items-center gap-3">
              <p className="text-sm text-[var(--color-text-secondary)] flex-1">{t('wh.multiTapHint')}</p>
              <button
                onClick={handlePrintAll}
                disabled={printing}
                className="shrink-0 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-bold disabled:opacity-50 flex items-center gap-2"
              >
                {printing ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
                {printing ? t('wh.multiPrinting') : `${t('wh.multiPrintAll')} (${pendingNow.length})`}
              </button>
            </div>
          )}
          {checklistMode && !allDone && (
            <p className="mt-3 text-sm font-semibold text-blue-700 dark:text-blue-300">
              {t('wh.multiChecklistHint')} · {closedShipments.size}/{eligible.length}
            </p>
          )}
          {allDone && products.length > 0 && (
            <p className="mt-3 text-sm font-semibold text-green-700 dark:text-green-300">
              {t('wh.multiAllDone')}
            </p>
          )}
        </div>

        {/* Tarjetas — una por producto del paquete */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {products.map(p => {
            const st = cardState(p);
            const printInfo = p.order ? printResults[p.order.order_id] : undefined;
            return (
              <div
                key={p.purchase_id}
                onClick={() => { if (!checklistMode && st.clickable && canOperate) onSelectProduct(p.purchase_id); }}
                className={`rounded-xl border-2 p-3 flex items-center gap-3 transition-colors ${st.style} ${checklistMode || !st.clickable || !canOperate ? 'cursor-default' : ''}`}
              >
                {p.thumbnail
                  ? <img src={hiRes(p.thumbnail) || undefined} alt="" className="w-16 h-16 object-contain rounded-lg bg-white shrink-0" />
                  : <div className="w-16 h-16 rounded-lg bg-[var(--color-bg)] flex items-center justify-center shrink-0"><Package size={24} className="text-gray-400" /></div>}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[var(--color-text)] line-clamp-2">{p.title}</p>
                  <p className="text-xs text-[var(--color-text-secondary)] font-mono">
                    {p.asin} · x{p.quantity}
                    {p.amazon_order_id ? ` · Amazon ${p.amazon_order_id}` : ''}
                    {p.order ? ` · ML ${p.order.ml_order_id}` : ''}
                    {p.order?.buyer_nickname ? ` · ${p.order.buyer_nickname}` : ''}
                  </p>
                  {p.cross_seller && (
                    <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">{p.seller.nickname}</span>
                  )}
                  {printInfo && !printInfo.ok && (
                    <p className="text-xs text-red-500 mt-1">{t('wh.multiLabelError')}: {printInfo.error}</p>
                  )}
                </div>
                {st.badge && (
                  <span className="shrink-0 flex items-center gap-1.5 text-xs font-semibold text-[var(--color-text-secondary)]">
                    {st.icon}{st.badge}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer checklist — escanear etiqueta ML para cerrar cada venta */}
        {checklistMode && pendingNow.length > 0 && canOperate && (
          <div className="shrink-0 p-3 sm:p-4 border-t-2 border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-900/10">
            <form onSubmit={handleChecklistScan} className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={scanCode}
                onChange={e => setScanCode(e.target.value)}
                placeholder={t('wh.scanTracking')}
                className="flex-1 min-w-0 px-3 sm:px-4 py-3 bg-[var(--color-bg)] border-2 border-[var(--color-border)] rounded-xl text-base sm:text-lg font-mono text-[var(--color-text)] outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/30"
                autoFocus
              />
              <button type="submit" disabled={scanBusy || !scanCode.trim()}
                className="shrink-0 px-4 sm:px-5 py-3 bg-green-600 text-white rounded-xl text-sm sm:text-base font-bold disabled:opacity-50 flex items-center gap-2"
              >
                {scanBusy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                <span className="hidden sm:inline">{t('wh.addAndClose')}</span>
              </button>
            </form>
            {scanError && (
              <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1.5">
                <AlertTriangle size={12} />{scanError}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
